import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { UserPlus } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import BackButton from "@/components/back-button";
import { RoleLink } from "@/components/role-link";
import type { RosterFilter } from "@/queries/api/dashboardV2Api";
import { useInfiniteRosterQuery } from "@/queries/hooks/rosterHooks";

import type { RosterSortKey } from "../dashboard/-components/v2/roster-table";
import {
  ROSTER_SORT_DEFAULT_DIR,
  RosterFilterChips,
  RosterItemsTable,
} from "../dashboard/-components/v2/roster-table";

import { InvitedWorkers } from "./-components/invited-workers";

// Same column sort + name/role filter as the Find cover table, kept in the URL
// and applied by the backend so they cover the whole roster, not just loaded pages.
const RosterSearchSchema = z.object({
  sort: z.string().optional(),
  dir: z.enum(["asc", "desc"]).optional(),
  q: z.string().optional(),
});

export const Route = createFileRoute("/app/$role/_app/roster/")({
  validateSearch: RosterSearchSchema,
  component: RosterPage,
});

function RosterPage() {
  const { t } = useTranslation("dashboard");
  const [filter, setFilter] = useState<RosterFilter>("all");
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const sortKey = (search.sort as RosterSortKey | undefined) ?? null;
  const sortDir = search.dir ?? (sortKey ? ROSTER_SORT_DEFAULT_DIR[sortKey] : "asc");
  const handleSort = (key: RosterSortKey) => {
    const nextDir =
      search.sort === key
        ? sortDir === "asc"
          ? "desc"
          : "asc"
        : ROSTER_SORT_DEFAULT_DIR[key];
    void navigate({ search: (prev) => ({ ...prev, sort: key, dir: nextDir }), replace: true });
  };

  // Debounced text filter → URL `q`.
  const [text, setText] = useState(search.q ?? "");
  useEffect(() => {
    setText((prev) => (prev.trim() === (search.q ?? "") ? prev : (search.q ?? "")));
  }, [search.q]);
  useEffect(() => {
    const q = text.trim() || undefined;
    if (q === (search.q || undefined)) return;
    const id = setTimeout(
      () => void navigate({ search: (prev) => ({ ...prev, q }), replace: true }),
      300,
    );
    return () => clearTimeout(id);
  }, [text, search.q, navigate]);
  const filterActive = Boolean(search.q);

  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteRosterQuery(filter, { sort: sortKey, dir: sortKey ? sortDir : null, q: search.q });

  const items = data?.pages.flatMap((p) => p.items) ?? [];
  const total = data?.pages[0]?.total ?? 0;
  const starred = data?.pages[0]?.starred_count ?? 0;

  return (
    <div className="flex flex-col w-full px-3 sm:px-6 md:px-8 pt-4 sm:pt-6 md:pt-8 pb-16 max-w-[1200px] mx-auto">
      <BackButton />
      <div className="flex items-center justify-between gap-3 mt-4 mb-4 flex-wrap">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h1 className="heading-1">{t("v2.roster.title")}</h1>
          {data ? (
            <span className="text-sm text-slate">
              {t("v2.roster.meta", { total, starred })}
            </span>
          ) : null}
        </div>
        <RoleLink
          to="/app/$role/roster/add"
          className="no-underline! inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-garnet text-white! text-sm font-semibold hover:opacity-90"
        >
          <UserPlus className="w-4 h-4" />
          {t("v2.roster.addMore")}
        </RoleLink>
      </div>

      <div className="mb-4">
        <RosterFilterChips filter={filter} onChange={setFilter} />
      </div>

      <section className="bg-white rounded-[15px] p-4 sm:p-5">
        <div className="mb-3">
          <input
            type="search"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("v2.roster.filterPlaceholder")}
            aria-label={t("v2.roster.filterPlaceholder")}
            className="h-9 w-full sm:w-72 rounded-lg border border-fog-dark px-3 text-sm text-garnet placeholder:text-slate/60 focus-visible:outline-2 focus-visible:outline-teal"
          />
        </div>
        {isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-14 rounded-lg bg-fog animate-pulse" />
            ))}
          </div>
        ) : isError ? (
          <div className="flex items-center gap-3 text-sm text-slate">
            {t("v2.errorSection")}
            <button type="button" onClick={() => refetch()} className="underline text-teal">
              {t("v2.retry")}
            </button>
          </div>
        ) : items.length === 0 && filterActive ? (
          <p className="py-8 text-center text-sm text-slate">{t("v2.roster.noMatch")}</p>
        ) : items.length === 0 ? (
          <div className="py-8 flex flex-col items-center gap-3 text-center">
            <p className="text-sm text-slate">{t("v2.roster.empty")}</p>
            <RoleLink to="/app/$role/roster/add" className="text-sm text-teal no-underline!">
              {t("v2.roster.addExisting")}
            </RoleLink>
          </div>
        ) : (
          <>
            <RosterItemsTable
              items={items}
              filter={filter}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={handleSort}
            />
            <div className="pt-4 flex justify-center">
              {hasNextPage ? (
                <button
                  type="button"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="h-10 px-5 rounded-lg border border-fog-dark text-sm text-garnet hover:bg-fog disabled:opacity-50"
                >
                  {isFetchingNextPage ? t("v2.loading") : t("v2.loadMore")}
                </button>
              ) : (
                <span className="text-xs text-slate">
                  {t("v2.roster.showingAll", { total })}
                </span>
              )}
            </div>
          </>
        )}
      </section>

      <InvitedWorkers />
    </div>
  );
}
